import { PlusIcon } from '@shopify/polaris-icons';
import { useEffect, useState } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  DataTable,
  Modal,
  FormLayout,
  TextField,
  Checkbox,
  Banner,
  Tabs,
} from '@shopify/polaris';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApi } from '../hooks/useApi';

const WAREHOUSE_APP_URL = (import.meta.env.VITE_WAREHOUSE_APP_URL as string | undefined) || 'https://warehouse.whenlyapp.com';

interface PickList {
  id: string;
  reference: string;
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  order_count: number;
  item_count: number;
  picked_count: number;
  assigned_to: string | null;
  created_at: string;
  completed_at: string | null;
}

interface Order {
  id: string;
  order_number: string;
  customer_name: string;
  delivery_date: string;
  item_count: number;
}

export default function PickLists() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { fetchApi, error } = useApi();
  const [pickLists, setPickLists] = useState<PickList[]>([]);
  const [selectedTab, setSelectedTab] = useState(0);

  // Create pick list
  const [createModalActive, setCreateModalActive] = useState(false);
  const [availableOrders, setAvailableOrders] = useState<Order[]>([]);
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
  const [pickListName, setPickListName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const tabs = [
    { id: 'pending', content: 'Pending' },
    { id: 'in_progress', content: 'In Progress' },
    { id: 'completed', content: 'Completed' },
    { id: 'all', content: 'All' },
  ];

  useEffect(() => {
    loadPickLists();
  }, [selectedTab]);

  useEffect(() => {
    // If order param exists, open create modal with that order selected (once, on arrival)
    const orderId = searchParams.get('order');
    if (orderId) {
      loadAvailableOrders().then(() => {
        setSelectedOrders([orderId]);
        setPickListName(`Pick-${new Date().toISOString().split('T')[0]}`);
        setCreateModalActive(true);
      });
    }
  }, []);

  async function loadPickLists() {
    const status = ['pending', 'in_progress', 'completed', 'all'][selectedTab];
    const params = status !== 'all' ? `?status=${status}` : '';
    const data = await fetchApi<{ pickLists: PickList[] }>(`/picking/lists${params}`);
    if (data?.pickLists) {
      setPickLists(data.pickLists);
    }
  }

  async function loadAvailableOrders() {
    const data = await fetchApi<{ orders: Order[] }>('/orders?status=allocated&limit=100');
    if (data?.orders) {
      setAvailableOrders(data.orders);
    }
  }

  const openCreateModal = async () => {
    await loadAvailableOrders();
    setSelectedOrders([]);
    setCreateError(null);
    setPickListName(`Pick-${new Date().toISOString().split('T')[0]}`);
    setCreateModalActive(true);
  };

  const closeCreateModal = () => { setCreateModalActive(false); setCreateError(null); };

  const handleCreatePickList = async () => {
    if (selectedOrders.length === 0 || creating) return;
    setCreating(true);
    setCreateError(null);
    const result = await fetchApi<{ success: boolean; id: number }>('/picking/lists', {
      method: 'POST',
      body: JSON.stringify({
        reference: pickListName,
        order_ids: selectedOrders,
      }),
    });
    setCreating(false);
    if (!result?.success) {
      // Keep the dialog open so the reason is visible next to the selection (WHENLY-75).
      setCreateError('The pick list could not be created.');
      return;
    }
    closeCreateModal();
    // New pick lists start as Pending; show that tab so the new list is visible.
    if (selectedTab === 0) await loadPickLists();
    else setSelectedTab(0);
  };

  const toggleOrderSelection = (orderId: string) => {
    setSelectedOrders((prev) =>
      prev.includes(orderId)
        ? prev.filter((id) => id !== orderId)
        : [...prev, orderId]
    );
  };

  const handleStartPicking = async (pickListId: string) => {
    await fetchApi(`/picking/lists/${pickListId}/start`, {
      method: 'POST',
    });
    loadPickLists();
  };

  const statusBadge = (status: PickList['status']) => {
    const config: Record<PickList['status'], { tone: 'success' | 'warning' | 'info' | 'critical'; label: string }> = {
      pending: { tone: 'warning', label: 'Pending' },
      in_progress: { tone: 'info', label: 'In Progress' },
      completed: { tone: 'success', label: 'Completed' },
      cancelled: { tone: 'critical', label: 'Cancelled' },
    };
    const { tone, label } = config[status];
    return <Badge tone={tone}>{label}</Badge>;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Page
      title="Pick Lists"
      primaryAction={{
        icon: PlusIcon, content: 'Create Pick List',
        onAction: openCreateModal,
      }}
      secondaryActions={[
        {
          content: 'Warehouse App',
          url: WAREHOUSE_APP_URL,
          external: true,
        },
      ]}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          <Card>
            <Tabs tabs={tabs} selected={selectedTab} onSelect={setSelectedTab} />
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              {pickLists.length > 0 ? (
                <DataTable
                  columnContentTypes={['text', 'text', 'numeric', 'text', 'text', 'text', 'text']}
                  headings={['Reference', 'Status', 'Orders', 'Progress', 'Assigned', 'Created', 'Actions']}
                  rows={pickLists.map((pl) => [
                    pl.reference,
                    statusBadge(pl.status),
                    pl.order_count.toString(),
                    `${pl.picked_count}/${pl.item_count}`,
                    pl.assigned_to || '-',
                    formatDate(pl.created_at),
                    <InlineStack gap="200">
                      {pl.status === 'pending' && (
                        <Button size="slim" onClick={() => handleStartPicking(pl.id)}>
                          Start
                        </Button>
                      )}
                      {pl.status === 'in_progress' && (
                        <Button size="slim" variant="primary" url={`${WAREHOUSE_APP_URL}/pick/${pl.id}`} external>
                          Continue
                        </Button>
                      )}
                      <Button size="slim" onClick={() => navigate(`/pick-lists/${pl.id}`)}>
                        View
                      </Button>
                    </InlineStack>,
                  ])}
                />
              ) : (
                <BlockStack gap="200" inlineAlign="center">
                  <Text as="p" tone="subdued">
                    No pick lists found.
                  </Text>
                  <Button icon={PlusIcon} onClick={openCreateModal}>Create your first pick list</Button>
                </BlockStack>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Quick Stats */}
        <Layout.Section variant="oneThird">
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Quick Stats</Text>
              <BlockStack gap="200">
                <InlineStack align="space-between">
                  <Text as="span">Pending</Text>
                  <Badge>{pickLists.filter((p) => p.status === 'pending').length.toString()}</Badge>
                </InlineStack>
                <InlineStack align="space-between">
                  <Text as="span">In Progress</Text>
                  <Badge tone="info">{pickLists.filter((p) => p.status === 'in_progress').length.toString()}</Badge>
                </InlineStack>
                <InlineStack align="space-between">
                  <Text as="span">Completed Today</Text>
                  <Badge tone="success">
                    {pickLists.filter((p) => {
                      if (p.status !== 'completed' || !p.completed_at) return false;
                      const today = new Date().toDateString();
                      return new Date(p.completed_at).toDateString() === today;
                    }).length.toString()}
                  </Badge>
                </InlineStack>
              </BlockStack>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>

      {/* Create Pick List Modal */}
      <Modal
        open={createModalActive}
        onClose={closeCreateModal}
        title="Create Pick List"
        primaryAction={{
          icon: PlusIcon, content: 'Create',
          onAction: handleCreatePickList,
          loading: creating,
          disabled: selectedOrders.length === 0 || creating,
        }}
        secondaryActions={[
          { content: 'Cancel', onAction: closeCreateModal, disabled: creating },
        ]}
        size="large"
      >
        <Modal.Section>
          <FormLayout>
            {createError && (
              <Banner tone="critical">
                <p>{createError}</p>
                {error && <p>{error}</p>}
              </Banner>
            )}
            <TextField
              label="Pick List Name"
              value={pickListName}
              onChange={setPickListName}
              autoComplete="off"
            />

            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text as="h3" variant="headingMd">Select Orders</Text>
                <Text as="span" tone="subdued">
                  {selectedOrders.length} selected
                </Text>
              </InlineStack>

              {availableOrders.length > 0 ? (
                <BlockStack gap="200">
                  {availableOrders.map((order) => (
                    <div
                      key={order.id}
                      style={{
                        padding: '0.75rem',
                        border: '1px solid #e1e3e5',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        background: selectedOrders.includes(order.id) ? '#f3f4f6' : 'white',
                      }}
                      onClick={() => toggleOrderSelection(order.id)}
                    >
                      <InlineStack align="space-between">
                        <BlockStack gap="100">
                          <Text as="span" variant="bodyMd" fontWeight="bold">
                            #{order.order_number}
                          </Text>
                          <Text as="span" tone="subdued">
                            {order.customer_name} • {order.item_count} items
                          </Text>
                        </BlockStack>
                        <BlockStack gap="100" inlineAlign="end">
                          <Badge>
                            {new Date(order.delivery_date).toLocaleDateString('en-GB', {
                              day: 'numeric',
                              month: 'short',
                            })}
                          </Badge>
                          <div onClick={event => event.stopPropagation()}>
                            <Checkbox
                              label={`Select order ${order.order_number}`}
                              labelHidden
                              checked={selectedOrders.includes(order.id)}
                              onChange={() => toggleOrderSelection(order.id)}
                            />
                          </div>
                        </BlockStack>
                      </InlineStack>
                    </div>
                  ))}
                </BlockStack>
              ) : (
                <Text as="p" tone="subdued">
                  No orders available for picking. Orders must be in 'Allocated' status.
                </Text>
              )}
            </BlockStack>
          </FormLayout>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
